"use client"

import { useState, lazy, Suspense } from 'react'
import * as stylex from '@stylexjs/stylex'
import { ImageGalleryItem } from '@/models/gallery'
import { styles } from '@/styles/ImagePage.stylex'

const ImagePopup = lazy(() => import('@/components/popup/ImagePopup'))

interface TextToImageGalleryProps {
    title: string
    description: string
    modelKey: string
    modelLabel: string
    modelNote: string
    items: ImageGalleryItem[]
}

export function TextToImageGallery({ title, description, modelKey, modelLabel, modelNote, items }: TextToImageGalleryProps) {
    const [showPopup, setShowPopup] = useState(false)
    const [selectedItem, setSelectedItem] = useState<ImageGalleryItem>({ id: '', src: '', className: '', prompt: '', width: 0, height: 0, aspect_ratio: '16:9' })

    const openPopup = (item: ImageGalleryItem) => {
        setSelectedItem(item)
        setShowPopup(true)
    }

    return (
        <>
            <div {...stylex.props(styles.gallerySection)}>
                <h3 {...stylex.props(styles.sectionTitle)}>{title}</h3>
                <p {...stylex.props(styles.galleryDescription)} dangerouslySetInnerHTML={{ __html: description }} />

                <div {...stylex.props(styles.galleryGrid)}>
                    {items.map((item, index) => (
                        <TextToImageItem
                            key={index}
                            item={item}
                            modelLabel={modelLabel}
                            onClick={() => openPopup(item)}
                        />
                    ))}
                </div>
            </div>

            <Suspense fallback={null}>
                {showPopup && (
                    <ImagePopup
                        isOpen={showPopup}
                        onClose={() => setShowPopup(false)}
                        id={selectedItem.id}
                        src={selectedItem.src}
                        modelKey={modelKey}
                        modelLabel={modelLabel}
                        prompt={selectedItem.prompt}
                        aspect_ratio={selectedItem.aspect_ratio}
                        popupTitle="Image Details"
                        modelNote={modelNote}
                        variants={selectedItem.variants}
                    />
                )}
            </Suspense>
        </>
    )
}

function TextToImageItem({ item, modelLabel, onClick }: { item: ImageGalleryItem, modelLabel: string, onClick: () => void }) {
    const [isHovered, setIsHovered] = useState(false)

    // Dynamic grid classes logic
    const dynamicLayout = [
        (item.variant === 'featured' || item.className?.includes('featured')) && styles.featured,
        (item.variant === 'wide' || item.className?.includes('wide')) && styles.wide,
        (item.variant === 'tall' || item.className?.includes('tall')) && styles.tall,
    ]

    return (
        <div
            {...stylex.props(styles.galleryItem, ...dynamicLayout)}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <img
                src={item.src}
                {...stylex.props(styles.galleryImage, isHovered && styles.galleryImageHover)}
                alt={`${modelLabel} gallery`}
                loading="lazy"
            />
            <div {...stylex.props(styles.galleryPrompt, isHovered && styles.galleryPromptVisible)}>
                <p {...stylex.props(styles.galleryPromptText)}>{item.prompt}</p>
            </div>
            <div {...stylex.props(styles.clickIndicator, isHovered && styles.clickIndicatorVisible)}>
                Click me
            </div>
        </div>
    )
}